import React from 'react';
import i18n from 'i18n';
import moment from 'moment';
import InscriptionType from 'constants/inscription-type';
import { get } from 'lodash';
import MessageCardType from 'constants/message-card-type';
import { ellipsisNL } from 'util/filters';
import SessionManager from 'util/session-manager';

export const keysTemplate = [
  'Name',
  'NameKana',
  'Postcode',
  'Prefecture',
  'City',
  'Building',
  'Company',
  'Telephone'
];

export const getAddressInfo = value => {
  const keys = Object.keys(value);

  if (keys.every(key => !value[key])) {
    return null;
  }

  return keysTemplate.map(tmpl => {
    const key = keys.find(k => k.includes(tmpl));

    switch (tmpl) {
      case 'Name':
        return `${value[key]} ${i18n.t('common:honorific')}`;
      case 'Postcode':
        return `${i18n.t('common:postcode')}${value[key]}`;
      default:
        return value[key];
    }
  });
};

export const renderAddressOneLine = value => {
  const info = getAddressInfo(value);

  if (info == null) {
    return i18n.t('common:notInput');
  }

  return info.join(' ');
};

export const renderAddressMultipleLines = value => {
  const info = getAddressInfo(value);

  if (info == null) {
    return '';
  }

  return info.filter(Boolean).join('\n');
};

export const renderWrappingInfo = (value, multiline = false) => (
  <React.Fragment>
    <span className="wrapping-type">{value.wrappingType}</span>
    {multiline && <br />}
    {value.wrappingType !==
      i18n.t('orderAggregators:common:order.wrappingType1Name') && (
      <React.Fragment>
        <span className="regular-weight">
          {i18n.t('orderAggregators:common:order.wrappingInfoTooltipLabel')}
        </span>
        <span className="wrapping-info-inscription">
          {`${i18n.t('common:inscriptionLabel')} ${
            isNaN(+value.inscriptionType)
              ? value.inscriptionType
              : value.inscription.join(',') || i18n.t('common:none')
          }`}
        </span>
        <span className="wrapping-info-shipper-name">{`${i18n.t(
          'common:shipperNameLabel'
        )} ${value.shipperName.join(',') || i18n.t('common:none')}`}</span>
      </React.Fragment>
    )}
  </React.Fragment>
);

export const renderWrappingInfoDetail = value =>
  renderWrappingInfo(value, true);

export const renderMessageCardInfo = (wrappingInfo, isEllipsis = false) => {
  switch (wrappingInfo.messageCardType) {
    case MessageCardType.input:
      return isEllipsis
        ? ellipsisNL(wrappingInfo.messageCard)
        : wrappingInfo.messageCard;
    case MessageCardType.whitePaper:
      return i18n.t('orderAggregators:common:order.messageCardTypeWhitePaper');
    default:
      return i18n.t('orderAggregators:common:order.messageCardTypeNone');
  }
};

export const getStoredData = () => {
  let storedData;
  try {
    storedData = SessionManager.load();
  } catch (error) {
    switch (error.name) {
      case 'NoSessionError':
      case 'SessionExpiredError':
      case 'LoadDataError':
        return null;
      default:
        throw error;
    }
  }
  return storedData;
};

export const handleWrappingInfoDialogSave = (props, value, isUpdateSession) => {
  const { fieldName, change, getState } = props;
  const {
    wrappingInfo: { messageCard, messageCardType }
  } = get(getState().values, fieldName);

  const data = (value => {
    if (
      value.wrappingType ===
      i18n.t('orderAggregators:common:order.wrappingType1Name')
    ) {
      return {
        messageCard,
        messageCardType,
        wrappingType: value.wrappingType,
        inscription: [''],
        shipperName: [''],
        inscriptionType: i18n.t('orderAggregators:common:order.kotobuki'),
        shipperNameType: InscriptionType.oneLine
      };
    }

    if (
      isNaN(+value.inscriptionType) ||
      value.inscriptionType === InscriptionType.none
    ) {
      value.inscription = [''];
    }

    if (value.inscriptionType === InscriptionType.oneLine) {
      value.inscription.length = 1;
    }

    if (value.shipperNameType === InscriptionType.none) {
      value.shipperName = [''];
    }

    if (value.shipperNameType === InscriptionType.oneLine) {
      value.shipperName.length = 1;
    }

    return {
      ...value,
      messageCard,
      messageCardType
    };
  })(value);

  change(`${fieldName}.wrappingInfo`, data);

  if (isUpdateSession) {
    let storedData = getStoredData();
    const ordersIndex = fieldName.match(/\d+/)[0];
    storedData.orders[ordersIndex].wrappingInfo = data;
    SessionManager.save(storedData);
  }
};

export const handleMessageCardDialogSave = (props, value, isUpdateSession) => {
  const { fieldName, change } = props;

  change(`${fieldName}.wrappingInfo.messageCardType`, value.messageCardType);
  change(
    `${fieldName}.wrappingInfo.messageCard`,
    value.messageCardType === MessageCardType.input ? value.messageCard : ''
  );

  if (isUpdateSession) {
    let storedData = getStoredData();
    const ordersIndex = fieldName.match(/\d+/)[0];
    storedData.orders[ordersIndex].wrappingInfo.messageCardType = value.messageCardType;
    storedData.orders[ordersIndex].wrappingInfo.messageCard = value.messageCard;
    SessionManager.save(storedData);
  }
};

export const handleValidatedOnChange = (props, val, isUpdateSession) => {
  const { fieldName, change, duration } = props;

  const expiredOn = moment(val, i18n.t('common:dateFormat.default'))
    .add(duration)
    .format(i18n.t('common:dateFormat.default'));
  change(`${fieldName}.purchaseItem.expiredOn`, expiredOn);
  if (isUpdateSession) {
    let storedData = getStoredData();
    if (storedData == null) {
      props.push('/error');
    }
    const ordersIndex = fieldName.match(/\d+/)[0];
    storedData.orders[ordersIndex].purchaseItem.expiredOn = expiredOn;
    SessionManager.save(storedData);
  }
};

export const updateReferringData = props => {
  const { index, updateReferringOrders } = props;
  if (updateReferringOrders) {
    return updateReferringOrders(index - 1);
  }
};

export const isInvalidReceiverValues = props => {
  const { fieldName, getState } = props;

  const {
    receiverName,
    receiverPostcode,
    receiverCity,
    recerverTelephone
  } = get(getState().values, `${fieldName}.receiver`);
  return [receiverName, receiverPostcode, receiverCity, recerverTelephone].some(
    value => value === ''
  );
};

export const isInvalidShipperValues = props => {
  const { fieldName, getState } = props;

  const { shipperName, shipperPostcode, shipperCity, shipperTelephone } = get(
    getState().values,
    `${fieldName}.shipper`
  );
  return [shipperName, shipperPostcode, shipperCity, shipperTelephone].some(
    value => value === ''
  );
};

export const referringDataTemplate = (data, role = 'receiver') => (
  <table>
    <tbody>
      <tr>
        <th>{i18n.t('orderAggregators:common:order.referringTable.name')}</th>
        <td>
          <div className="cell-wrapper">{data[`${role}Name`]}</div>
        </td>
      </tr>
      <tr>
        <th>{i18n.t('orderAggregators:common:order.referringTable.kana')}</th>
        <td>
          <div className="cell-wrapper">
            {data[`${role}NameKana`] ? data[`${role}NameKana`] : '-'}
          </div>
        </td>
      </tr>
      <tr>
        <th>
          {i18n.t('orderAggregators:common:order.referringTable.address')}
        </th>
        <td>
          <p>{i18n.t('common:postcode') + data[`${role}Postcode`]}</p>
          <p>
            {data[`${role}Prefecture`] +
              data[`${role}City`] +
              data[`${role}Building`]}
          </p>
        </td>
      </tr>
      <tr>
        <th>
          {i18n.t('orderAggregators:common:order.referringTable.company')}
        </th>
        <td>
          <div className="cell-wrapper">
            {data[`${role}Company`] ? data[`${role}Company`] : '-'}
          </div>
        </td>
      </tr>
      <tr>
        <th>{i18n.t('orderAggregators:common:order.referringTable.tel')}</th>
        <td>
          <div className="cell-wrapper">{data[`${role}Telephone`]}</div>
        </td>
      </tr>
    </tbody>
  </table>
);



// WEBPACK FOOTER //
// ./src/components/OrderAggregators/scripts/order.js